/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import { Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { SimpleIndefiniteProgressDialogComponent } from "app/components/simple-indefinite-progress-dialog/simple-indefinite-progress-dialog.component";
import { ERROR_DIALOG_TITLE, SimpleSuccessErrorDialogComponent } from "app/components/simple-success-error-dialog-box/simple-success-error-dialog-box.component";
import { MmcGetDispatchCommandStatusDto } from "app/models/mmc/mmc-get-dispatch-command-status-dto";
import { MmcPrintDispatch } from "app/models/mmc/mmc-print-dispatch";
import { GetMaterialInfoResponseDto, KissService, PrintMaterialsRequestDto, PrintMaterialsResponseDto, UomDto } from "app/services/kiss.service";
import { MmcService } from "app/services/mmc.service";
import { CookieService } from "ngx-cookie-service";

const CookieKey_SelectedPrinter = 'page[kiss-print]-key[selected-printer]';

@Component({
    templateUrl: './kiss-print-page.component.html',
    styleUrls: ['./kiss-print-page.component.css']
  })
export class KissPrintPageComponent implements OnInit, OnDestroy {

  /** Progress Dialog displayed when performing actions to give the User an indication something is happening... */
  @ViewChild(SimpleIndefiniteProgressDialogComponent) indefiniteProgressDialog: SimpleIndefiniteProgressDialogComponent;

  /** Reference to the SimpleSuccessErrorDialog used to display error */
  @ViewChild('successErrorDialog') 
  simpleSuccessErrorDialogComponent: SimpleSuccessErrorDialogComponent;

  /*
   * Variables bound by UI Elements / Inputs
   */
  batchNumber: string = "";
  lotNumber: string = "";
  pao: string = "";
  customerSku: string = "";
  validationDate: string = "";
  materialNumber: string = "";
  materialDescription: string = "";
  selectedUom: UomDto;
  selectedPrinter: string | null;
  quantity: number | null;

  /** Available UOMs for the entered / loaded Material.  Used to populate the UOM Radio Buttons after loading the Material Info */
  availableUoms: UomDto[];

  /** Available Printers.  Used to populate the Printers combobox */
  availablePrinters: string[] = [];

  /** Flag used to indicate if / when Printing is successful.  Used to show / hide the "Print Success" indicator. */
  printStatus: string;

  /** Flag used to indicate if the top card (Production Info) should be disabled or not */
  formDisabled: boolean;

  /** Flag used to indicate if the data for the Material has been loaded and should be displayed or not */
  dataloaded: boolean;

  isUsingMmc: boolean = false;
  mmcPrintDispatch: MmcPrintDispatch = null;
  mmpiPrintResponse: PrintMaterialsResponseDto = null;
  isPrintBeingProcessed: boolean = false;

  isDestroyed: boolean = false;

  /** Constructor */
  constructor(
    private kissService: KissService,
    private mmcService: MmcService,
    private cookieService: CookieService
  ) { }

  /**
   * Initializes the component.  
   * Called automatically by the Angular Component Lifecycle.
   */
  async ngOnInit(): Promise<void> {
    this.isDestroyed = false;

    // Refresh the cached reference to the backend "Kiss Service" Instance in Edgefinity
    await this.kissService.refreshKissServiceReference();

    this.isUsingMmc = await this.kissService.isMmcBeingUsed();

    // Clear the data so that the UI is in a fresh state
    this.clearData();

    // Load the available printers (TODO: This should probably be done in a resolver in the background while navigating to the page)
    await this.loadAvailablePrinters();
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
  }

  /**
   * Loads / Refreshes the "Printers" that are available to the User.
   * This also will auto-select the previously used Printer (stored in a cookie) if it is available.
   */
  private async loadAvailablePrinters(): Promise<void> {
    let printers: string[] = null;
    try {
      printers = await this.kissService.getAvailablePrinters();
    } catch (error) {
      this.handleError(error);
      return;
    }
    while (this.availablePrinters.length > 0) {
      this.availablePrinters.pop();
    }
    if (printers?.length > 0) {
      printers.sort();
      this.availablePrinters.push(...printers);
    }

    // Preselect the previously used Printer (stored as a cookie value) if it is available
    const oldSelectedPrinter = this.getStoredSelectedPrinter();
    const currentSelectedPrinter = this.availablePrinters.find(printer => printer == oldSelectedPrinter);
    this.selectedPrinter = currentSelectedPrinter == undefined? null : currentSelectedPrinter;
  }

  /**
   * Called when the User enters a Material Number and clicks the "Load Data" button.
   * This queries for and loads the Material Info for the entered Material Number.
   */
  async loadData(): Promise<void> {
    this.indefiniteProgressDialog.show("Loading", "Loading Info...");

    let response: GetMaterialInfoResponseDto = null;
    try {
      response = await this.kissService.getMaterialInfo(this.materialNumber);
    } catch (error) {
      this.indefiniteProgressDialog.hide();
      this.handleError(error);
      return;
    }

    this.materialNumber = response.material_number;
    this.materialDescription = response.material_description;
    this.availableUoms = response.uoms;
    this.formDisabled = true;
    this.dataloaded = true;
    this.indefiniteProgressDialog.hide();
  }

  /**
   * Called on Component initialization and when the "Clear / Reset" button is clicked.
   * This resets the UI to a blank state as if the User just navigated to the page, enabling the top 
   * portion / fields and allowing the User to search using a new Material Number.
   */
  clearData(): void {
    this.formDisabled = false;
    this.dataloaded = false;
    this.materialNumber = "";
    this.materialDescription = "";
    this.batchNumber = "";
    this.lotNumber = "";
    this.pao = "";
    this.customerSku = "";
    this.validationDate = "";
    this.selectedUom = null;
    this.quantity = null;
    this.printStatus = null;
  }

  /**
   * Called when the "Print" button is clicked. 
   * This calls Edgefinity to print the labels with the information entered / selected by the User.
   * This also stores the selected "Printer" option in the Cookie so it can be auto-selected when the User
   * navigates back to this page.
   */
  print(): void {
    if (this.isPrintBeingProcessed) {
      return;
    }

    this.printStatus = null;
    this.isPrintBeingProcessed = true;
    console.log(`print() - Using MMC? '${this.isUsingMmc}'...`);
    this.indefiniteProgressDialog.show("Printing", "Printing via " + (this.isUsingMmc? "MMC" : "MMPI") + "...");
    if (this.isUsingMmc) {
      this.printUsingMmc();
    } else {
      this.printUsingMmpi();
    }
  }

  private async printUsingMmpi(): Promise<void> {
    console.log(`printUsingMmpi()`);
    // Reset the "Print Status" back to null so that the "Complete" indicator is hidden
    this.printStatus = null;

    // Build the Request Objects
    const printData: PrintMaterialsRequestDto = {
      material_number: this.materialNumber,
      material_description: this.materialDescription,
      batch_number: this.batchNumber,
      lot_number: this.lotNumber,
      pao: this.pao,
      validation_date: this.validationDate,
      misc01: this.customerSku, //String (Use this to pass the Customer SKU Number)
      misc02: "",
      misc03: "",
      misc04: "",
      misc05: "",
      uom: this.selectedUom,
      printer: this.selectedPrinter,
      quantity: this.quantity
    } as PrintMaterialsRequestDto;

    // Save the selected Printer option
    this.setStoredSelectedPrinter(this.selectedPrinter);

    // Call the Print API
    let data: PrintMaterialsResponseDto = null;
    try {
      data = await this.kissService.printUsingMmpi(printData).toPromise();
    } catch (error) {
      this.indefiniteProgressDialog.hide();
      this.isPrintBeingProcessed = false;
      this.handleError(error);
      return;
    }

    this.printStatus = 'success'
    this.mmpiPrintResponse = data;
    this.indefiniteProgressDialog.hide();
    this.isPrintBeingProcessed = false;
  }

  private async printUsingMmc(): Promise<void> {
    console.log(`printUsingMmc()`);
    // Reset the "Print Status" back to null so that the "Complete" indicator is hidden
    this.printStatus = null;

    // Build the Request Objects
    const printData: PrintMaterialsRequestDto = {
      material_number: this.materialNumber,
      material_description: this.materialDescription,
      batch_number: this.batchNumber,
      lot_number: this.lotNumber,
      pao: this.pao,
      validation_date: this.validationDate,
      misc01: this.customerSku, //String (Use this to pass the Customer SKU Number)
      misc02: "",
      misc03: "",
      misc04: "",
      misc05: "",
      uom: this.selectedUom,
      printer: this.selectedPrinter,
      quantity: this.quantity
    } as PrintMaterialsRequestDto;

    // Save the selected Printer option
    this.setStoredSelectedPrinter(this.selectedPrinter);

    // Call the Print API
    let data: MmcPrintDispatch = null;
    try {
      data = await this.kissService.printUsingMmc(printData).toPromise();
    } catch (error) {
      this.indefiniteProgressDialog.hide();
      this.isPrintBeingProcessed = false;
      this.printStatus = 'error';
      this.handleError(error);
      return;
    }

    this.indefiniteProgressDialog.hide();
    this.isPrintBeingProcessed = false;
    this.mmcPrintDispatch = data;
    if (data?.dispatches?.length > 0 && data.dispatches[0].id != null) {
      this.printStatus = 'pending';
      setTimeout(this.refreshDispatch.bind(this, data), 300);
    } else {
      this.printStatus = 'error';
    }
  }

  private async refreshDispatch(printDispatch: MmcPrintDispatch): Promise<void> {
    if (this.isDestroyed) {
      console.warn(`Stopping Print Dispatch refresh as the Component was destroyed!`);
      return;
    } else if (printDispatch == null) {
      console.warn(`Stopping Print Dispatch refresh as the this Dispatch is null!`);
      return;
    } else if (printDispatch != this.mmcPrintDispatch) {
      console.warn(`Stopping Print Dispatch refresh as the this Dispatch doesn't match!`);
      return;
    }

    const dispatchId = printDispatch.dispatches[0].id;

    let commandStatus: MmcGetDispatchCommandStatusDto = null;
    try {
      commandStatus = await this.mmcService.getDispatchCommandStatus(dispatchId);
    } catch (error) {
      console.error(`Error obtaining Dispatch Status for ID: '${dispatchId}'... Error: ${error}`);
    }

    if (!this.isDestroyed && printDispatch == this.mmcPrintDispatch) {
      if (commandStatus?.printStatus != null) {
        this.printStatus = commandStatus.printStatus;
      }

      // Schedule another refresh
      setTimeout(this.refreshDispatch.bind(this, printDispatch), 300);
    }
  }

  /**
   * Called to handle errors when calling APIs.  
   * Currently this prints them out to the console and displays an alert to the User.
   */
  handleError(error: any): void {
    console.error(`Error detected: ${error}`);
    /* New handling (TJB 04/18/2023) */
    if (error?.error != null) {
      this.simpleSuccessErrorDialogComponent.showErrorDialog(ERROR_DIALOG_TITLE, error.error);
    }
    
    /* Default back to old handling */
    else if (error != null) {
      const response: Response = error;
      alert(response.text());
    }
  }

  /**
   * Returns the previously selected Printer option which is stored as a cookie.
   */
  private getStoredSelectedPrinter(): string {
    return this.cookieService.get(CookieKey_SelectedPrinter);
  }

  /**
   * Sets the previously selected Printer option which is stored as a cookie.
   */
  private setStoredSelectedPrinter(selectedPrinter: string): void {
    this.cookieService.set(CookieKey_SelectedPrinter, selectedPrinter, 1, '/', null, false, 'Lax');
  }

}
